/**
 * ColumnsSection Component
 *
 * Dynamic columns section (max 4 columns) with per-column collections of primitives.
 * Uses Bootstrap grid utilities (row-cols-*, g-*) and border/rounded classes.
 */
import React from "react";
import { filterClass } from "../../utils/class-utils";
import { getHorizontalContentAlign, getSectionBlockAlignClass } from "../utils/content-alignment";
import type { ColumnsSectionProps } from "../types";

const clampInt = (value: string | undefined, min: number, max: number, fallback: number) => {
  const n = parseInt(value || "", 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
};

/** Responsive row-cols classes: stacks to 1 col on md and below. */
function responsiveRowColsClasses(numColumns: number): string[] {
  if (numColumns <= 2) return ["row-cols-1", "row-cols-md-2"];
  if (numColumns === 3) return ["row-cols-1", "row-cols-md-2", "row-cols-xl-3"];
  return ["row-cols-1", "row-cols-md-2", "row-cols-xl-4"];
}

export function ColumnsSection(props: ColumnsSectionProps) {
  const {
    columns = "2",
    paddingYClass = "py-section-md",
    bgClass = "no-class",
    verticalAlignClass = "justify-content-start",
    contentAlignClass = "align-items-start text-start",

    Column1,
    Column2,
    Column3,
    Column4,
  } = props;

  const numColumns = clampInt(columns, 1, 4, 2);
  const inheritedContentAlign = getHorizontalContentAlign(contentAlignClass) ?? "start";
  const columnCollections: Array<ColumnsSectionProps["Column1"]> = [Column1, Column2, Column3, Column4].slice(0, 4);

  const sectionClasses = [
    bgClass,
    filterClass(paddingYClass),
  ]
    .map(filterClass)
    .filter(Boolean)
    .join(" ");

  const containerClasses = [
  ]
    .filter(Boolean)
    .join(" ");

  /**
   * ===== CLASSES (frontend can edit here) =====
   */
  const classes = {
    section: ["section section-columns", sectionClasses].filter(Boolean).join(" ").trim(),
    container: ["container-xl", containerClasses].filter(Boolean).join(" ").trim(),
    row: ["row", ...responsiveRowColsClasses(numColumns), "row-gap-5"].join(" "),
    col: ["col d-flex flex-column gap-4", filterClass(verticalAlignClass), getSectionBlockAlignClass(contentAlignClass)].filter(Boolean).join(" "),
    item: "w-100 component-item",
  };

  return (
    <section className={classes.section || undefined}>
      <div className={classes.container || undefined}>
        <div className={classes.row}>
          {columnCollections.slice(0, numColumns).map((items, colIdx) => (
            <div key={colIdx} className={classes.col}>
              {items?.map((item, itemIdx) => {
                const ItemType = item.type as React.ComponentType<any>;
                // eslint-disable-next-line react/no-array-index-key
                return (
                  <div key={itemIdx} className={classes.item}>
                    <ItemType {...item.props} inheritedContentAlign={inheritedContentAlign} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default ColumnsSection;


